/**
 * The app shell — a chat-first, Claude-shaped layout: **one** sidebar, and the
 * pane filling the rest.
 *
 * The connected instance sits in the title bar (the sidebar's instance card
 * only where there is no title bar, i.e. a plain browser). The sidebar reads top
 * to bottom as "what am I working in, what can I do, what have I been doing": the
 * current project, New chat / Revit Database / Skills / Plugins, the Chats section, the
 * account. It used to be two sidebars and a footer — the connected Revit in a
 * strip at the very bottom, and the Revit Database, the history and the project
 * chip in a rail on the chat's right. The team's report was that the connected
 * Revit was easy to miss down there and that two sidebars were one too many, so
 * the footer is gone and the rail's contents live here.
 *
 * Three decisions carried over from the previous shell, still true:
 *
 * *The agent status is chrome, not a pane.* It can change under the user at any
 * moment — the agent can die and come back on a new port (item K) — so it shows
 * under the instance card, on every route, the moment it is not ready, rather
 * than on a Settings screen nobody is looking at when it fails.
 *
 * *The pane is a pure function of the route*, so "each nav item swaps the view"
 * is a property of a table rather than of an event handler.
 *
 * *The panes past chat are placeholders and the chrome is not.* Item AE fills the
 * chat's backend; items R/AD fill Plugins; the sidebar, routing and account row
 * are the shell.
 */

import { Fragment, useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import type { Session } from "@supabase/supabase-js";
import { useAuth } from "../contexts/AuthProvider";
import { useRoute } from "../hooks/useRoute";
import { useLocale } from "../i18n/LocaleProvider";
import type { HostsView } from "../hooks/useHosts";
import type { PluginsController } from "../hooks/usePlugins";
import type { AgentTarget } from "../lib/agentClient";
import { InstanceCard, InstanceChips } from "./InstanceCard";
import { ReportIssueDialog } from "./ReportIssueDialog";
import { SidebarChats } from "./SidebarChats";
import { ChatSessions } from "../lib/chatSessions";
import { SkillEditSessions } from "../lib/skillEditSessions";
import {
  instanceAllowed,
  primarySelection,
  resolveSelections,
  selectionFor,
  targetSessions,
  type InstanceRestriction,
  type Selection,
  type Selections,
  type VendorPicks,
} from "../lib/selectedInstance";
import { groupByVendor, type HostKind } from "../lib/hostStatus";
import ThreadsPane from "../panes/ThreadsPane";
import WorkflowsPane from "../panes/WorkflowsPane";
import { FamilyBuilderHost } from "../panes/FamilyBuilderPane";
import ChatsPane from "../panes/ChatsPane";
import PluginsPane from "../panes/PluginsPane";
import SkillsPane from "../panes/SkillsPane";
import ProjectsPane from "../panes/ProjectsPane";
import DashboardPane from "../panes/DashboardPane";
import { BetaFeaturesCard } from "./settings/BetaFeaturesCard";
import { useWorkflowList, type WorkflowListState } from "../workflows/useWorkflowSections";
import { useWorkflowPreferences } from "../workflows/useWorkflowPreferences";
import SettingsPane from "../panes/SettingsPane";
import UpdatePassword from "../pages/UpdatePassword";
import { NAV_ROUTES, ROUTE_LABEL_KEYS, type RouteId } from "../routes";
import { sidebarStore } from "../lib/sidebarStore";
import { SIDEBAR_DRAWER_QUERY, closeDatabase, openDatabase, resolveSidebarView, sidebarShown, type SidebarView } from "../lib/sidebarView";
import { titlebarSlot } from "../lib/titlebarSlot";
import { useMediaQuery } from "../hooks/useMediaQuery";
import { useThreads, type ThreadsState } from "../hooks/useThreads";
import type { OrgState } from "./OrgSwitcher";
import { useSelectedProject } from "../hooks/useSelectedProject";
import { useProjects, type ProjectsState } from "../hooks/useProjects";
import { hasAction, useUserPermissions, type PermissionsState } from "../hooks/usePermissions";
import { createProject, deleteProject, updateProject } from "../lib/projects";
import { resolveSelection } from "../lib/selectProject";
import { API_BASE_URL } from "../lib/config";
import type { ProjectDraft } from "../components/projects/ProjectModal";
import type { Organization } from "../lib/organizations";
import "../ui/brand.css";

export interface AppInfo {
  version: string;
  bridgeVersion: number;
  environment: string;
  /** OS family from `process.platform` — `"win32"` on all Windows, regardless
   *  of bitness. The architecture is `arch`. */
  platform: string;
  /** CPU architecture from `process.arch` (`"x64"`, `"arm64"`, `"ia32"`) — this
   *  is what tells a 64-bit build from a 32-bit one. Optional: an older desktop
   *  bridge omits it, and the renderer (deployed separately) will meet those. */
  arch?: string;
}

/**
 * Nav items for features that are open but still beta; their label carries a
 * Beta pill (owner, 2026-10-21). The pane's own title carries one too.
 */
const BETA_ROUTES: ReadonlySet<RouteId> = new Set(["workflows"]);

/** Small line icons for the nav items — inline so the CSP has nothing to block. */
const NAV_ICON: Record<RouteId, React.ReactNode> = {
  dashboard: <Icon path="M4 20V10M10 20V4M16 20v-7M22 20H2" />,
  threads: <Icon path="M4 5h16v10H8l-4 4V5z" />,
  chats: <Icon path="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8M3 3v5h5M12 7v5l4 2" />,
  projects: <Icon path="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z" />,
  skills: <Icon path="m18 16 4-4-4-4M6 8l-4 4 4 4m8.5-12-5 16" />,
  workflows: <Icon path="M4 4h6v6H4zM14 14h6v6h-6zM10 7h4a3 3 0 0 1 3 3v4" />,
  // lucide `Box` — v2's Family Builder glyph.
  "family-builder": <Icon path="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16ZM3.3 7l8.7 5 8.7-5M12 22V12" />,
  plugins: <Icon path="M9 3v4M15 3v4M6 7h12v6a6 6 0 0 1-12 0z" />,
  settings: <Icon path="M9.7 4.3 10.2 2h3.6l.5 2.3 2.1 1.2 2.2-.7 1.8 3.1-1.7 1.6v2.4l1.7 1.6-1.8 3.1-2.2-.7-2.1 1.2-.5 2.3h-3.6l-.5-2.3-2.1-1.2-2.2.7-1.8-3.1 1.7-1.6V9.5L3.6 7.9l1.8-3.1 2.2.7 2.1-1.2ZM12 7a4 4 0 1 0 0 8 4 4 0 0 0 0-8Z" />,
  "update-password": null,
};

export default function AppShell({
  session,
  agent,
  info,
  plugins,
  hosts,
  organizations,
  selectedOrgId,
  onSelectOrg,
}: {
  session: Session | null;
  agent: AgentTarget;
  info: AppInfo | null;
  /** Started by App immediately after sign-in and retained across the pre-shell loading gates. */
  plugins: PluginsController;
  hosts: HostsView;
  /**
   * Fetched by `App`, not here (#63): the organization check is one of the gates
   * *before* this component, so the shell is never drawn for a user who is about
   * to be blocked. Passing the result down rather than calling the hook again
   * also keeps it to one request.
   */
  organizations: OrgState;
  /**
   * Which org is being worked in, owned by `App` (#63). The gate that blocks a
   * lapsed org is decided up there and replaces this component entirely, so the
   * selection it judges and the selection the panes read have to be the same
   * one — and switching organizations has to work from the gate, where this
   * component does not exist.
   */
  selectedOrgId: string | null;
  onSelectOrg: (id: string) => void;
}) {
  const { route, navigate } = useRoute();
  const workflowList = useWorkflowList(agent);
  const { enabledIds, setEnabled } = useWorkflowPreferences(session?.user?.id ?? null);
  const { t } = useLocale();
  const orgs = organizations.kind === "loaded" ? organizations.organizations : [];
  const currentOrg = orgs.find((o) => o.id === selectedOrgId) ?? orgs[0] ?? null;
  const orgName = currentOrg?.name ?? null;
  const orgId = currentOrg?.id ?? null;
  // Which project the app is working on. Held here rather than in the projects
  // pane because chat is bound to it (item AE) — see `useSelectedProject`.
  const { selectedProjectId, selectProject, forgetSelection } = useSelectedProject(orgId);
  // Which conversation the chat has open, or null for a new one. Held here
  // rather than in the pane because the sidebar's "New chat" clears it, and
  // because a thread belongs to a project — changing project must not leave the
  // chat showing the previous project's transcript.
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  // Bumped by "New", so the chat starts a fresh conversation even when none was
  // open — a chat sent a moment ago has no thread yet (#193).
  const [newChat, setNewChat] = useState(0);
  /**
   * The workflow entered from its card (`docs/proposals/workflows.md`, stage 1),
   * for the conversation it starts. Cleared by anything that opens a different
   * chat; a saved workflow conversation is recognised by the chat pane itself.
   */
  const [enteredWorkflow, setEnteredWorkflow] = useState<string | null>(null);
  /**
   * Every conversation's live state (#193). Here rather than in the chat pane so
   * the sidebar can list the ones still running; stopped all at once when the
   * shell goes, which is sign-out or an organization gate.
   */
  const [chatSessions] = useState(() => new ChatSessions());
  useEffect(() => () => chatSessions.abortAll(), [chatSessions]);
  /**
   * Every skill's Edit-with-AI conversation (#196) — here for the same reasons: an
   * edit keeps running when the editor closes, and the sidebar lists it.
   */
  const [skillEdits] = useState(() => new SkillEditSessions());
  useEffect(() => () => skillEdits.abortAll(), [skillEdits]);
  /** A skill the Running list asked the Skills pane to open; dropped once it has. */
  const [openSkill, setOpenSkill] = useState<{ id: string } | null>(null);
  const openSkillHandled = useCallback(() => setOpenSkill(null), []);
  /**
   * A conversation opened from the sidebar's Running list in another project. The
   * project switch below would clear the open thread; this is the one it opens
   * instead, once that switch has landed.
   */
  const pendingThreadRef = useRef<{ projectId: string; threadId: string } | null>(null);
  // Fetched here, not in the pane, because two panes need it: Projects lists it
  // and the chat names the one that is selected. One fetch, two readers.
  const projects = useProjects(
    orgId,
    session?.access_token ?? null,
    // Before the organisations land there is no `orgId`, and the projects pane
    // must read that as "still loading" rather than "no organization selected".
    organizations.kind === "loading",
  );
  // The stored choice is a hint, not the answer: it can name a project that has
  // been deleted or that this user can no longer see. `resolveSelection` decides
  // against the list that actually came back.
  const resolved =
    projects.kind === "loaded"
      ? resolveSelection(projects.projects, selectedProjectId)
      : { projectId: selectedProjectId, storedWasStale: false };
  const effectiveProjectId = resolved.projectId;
  const selectedProject =
    projects.kind === "loaded"
      ? (projects.projects.find((p) => p.id === effectiveProjectId) ?? null)
      : null;

  // Write the resolution back, so a stale entry is replaced rather than left to
  // rot and re-resolve on every render. Guarded on a real change, or this would
  // be a setState loop.
  useEffect(() => {
    if (projects.kind !== "loaded") return;
    if (!effectiveProjectId) return;
    if (effectiveProjectId === selectedProjectId) return;
    selectProject(effectiveProjectId);
  }, [projects.kind, effectiveProjectId, selectedProjectId, selectProject]);

  // Frontend gating only — api-v2's require_action is the authority. It decides
  // whether the control is offered, never whether the write is allowed.
  const perms = useUserPermissions(orgId, session?.access_token ?? null);
  const token = session?.access_token ?? null;
  const reloadProjects = projects.reload;
  // Reuse App's preloaded plugin and host detection across both panes and new chats.
  const reloadPlugins = plugins.reload;
  const previousPluginView = useRef({ route, activeThreadId, newChat });
  useEffect(() => {
    const previous = previousPluginView.current;
    previousPluginView.current = { route, activeThreadId, newChat };
    const returningHome = route === "threads" && activeThreadId === null && (
      previous.route !== "threads" || previous.activeThreadId !== null || previous.newChat !== newChat
    );
    const openingPlugins = route === "plugins" && previous.route !== "plugins";
    if (returningHome || openingPlugins) reloadPlugins();
  }, [route, activeThreadId, newChat, reloadPlugins]);
  const instances = hosts.kind === "loaded" ? hosts.instances : [];

  /**
   * The one CAD instance the app is driving.
   *
   * Held here, not in a pane: chat, skill execution and skill editing all
   * address it, and a selection owned by one screen is a selection the others
   * would have to guess at. Null means "no explicit choice" — resolved to the
   * first connected instance, never to nothing.
   */
  // One pick per vendor (#5). Resolved against what is running on every
  // render, so a closed pick shows as `gone` in its own control and nowhere else.
  const [picks, setPicks] = useState<VendorPicks>({});
  const selections = resolveSelections(picks, instances);
  // The single-id readers — document context, starter prompts — take Revit's
  // selection when there is one; see `primarySelection`.
  const selection = primarySelection(selections);
  const grouped = groupByVendor(instances);
  /**
   * The years a vendor's picker may switch to: an open AutoCAD skill's supported years,
   * set by the Skills pane while the skill is open (owner, 2026-10-24). The picker greys the
   * rest out, and a pick of one is refused here too, so no path around the list switches.
   */
  const [instanceRestriction, setInstanceRestriction] = useState<InstanceRestriction | null>(null);
  const pickFor = (kind: HostKind) => (sessionId: string) => {
    const instance = grouped[kind]?.find((candidate) => candidate.sessionId === sessionId);
    if (!instanceAllowed(instanceRestriction, kind, instance)) return;
    setPicks((prev) => ({ ...prev, [kind]: sessionId }));
  };

  /**
   * Whether a chat turn is streaming, lifted out of the pane.
   *
   * The instance control lives in the sidebar and the turn that pins it lives in
   * the chat, so one of the two has to know about the other. The signal travels
   * up because the sidebar is chrome — visible from every route — and the pane is
   * not.
   */
  const [chatBusy, setChatBusy] = useState(false);
  const [familyBusy, setFamilyBusy] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);

  /**
   * The current project's conversations. Fetched here, not in the chat pane,
   * because three things read the one list: the sidebar's Chats section, the
   * Chats page, and the chat itself, which only needs to say "reload" when a turn
   * creates or settles a thread.
   */
  const threads = useThreads(effectiveProjectId, token);
  const reloadThreads = threads.reload;

  // The save handlers live here because the list they refresh does too. Each
  // rethrows so the modal can show what the server said and stay open.
  const onCreateProject = async (draft: ProjectDraft): Promise<void> => {
    if (!orgId) throw new Error("no organization selected");
    const created = await createProject(
      token,
      {
        organization_id: orgId,
        name: draft.name,
        ...(draft.description ? { description: draft.description } : {}),
        ...(draft.notes ? { notes: draft.notes } : {}),
      },
      { apiBaseUrl: API_BASE_URL() },
    );
    // Adopt a newly created project **only when nothing is selected** — the case
    // of a first project, where the chat would otherwise still say none is
    // chosen. Never override an existing choice: creating a project is not a
    // request to stop working on the current one.
    if (created?.id && !selectedProjectId) selectProject(created.id);
    reloadProjects();
  };

  const onDeleteProject = async (projectId: string): Promise<void> => {
    await deleteProject(token, projectId, { apiBaseUrl: API_BASE_URL() });
    // Do not leave the chat bound to a project that no longer exists. The next
    // resolution picks a live one; clearing first stops the dead id being
    // written back as though it were still valid.
    if (projectId === selectedProjectId) forgetSelection();
    reloadProjects();
  };

  const onUpdateProject = async (projectId: string, draft: ProjectDraft): Promise<void> => {
    await updateProject(
      token,
      projectId,
      { name: draft.name, description: draft.description, notes: draft.notes },
      { apiBaseUrl: API_BASE_URL() },
    );
    reloadProjects();
  };

  // A thread is scoped to one project; carrying it across a switch would show a
  // transcript that does not belong to what is now selected.
  useEffect(() => {
    const pending = pendingThreadRef.current;
    pendingThreadRef.current = null;
    setActiveThreadId(pending && pending.projectId === selectedProjectId ? pending.threadId : null);
  }, [selectedProjectId]);

  /** Back to a running conversation from the sidebar — in its own project. */
  const openConversation = (threadId: string, projectId: string | null) => {
    navigateAnd(() => {
      setEnteredWorkflow(null);
      if (projectId && projectId !== effectiveProjectId) {
        pendingThreadRef.current = { projectId, threadId };
        selectProject(projectId);
      } else {
        setActiveThreadId(threadId);
      }
      navigate("threads");
    });
  };

  const name = displayName(session);

  /**
   * The rail's two compact states, both in the shared store because the only
   * toggle is the titlebar's (a sibling): `collapsed` is the persisted desktop
   * preference; `mobileOpen` the narrow viewport's transient drawer — closed
   * by the scrim, by navigating, and by never being persisted.
   */
  // The titlebar's toggle is a sibling of this component, so it cannot see
  // whether the rail exists — it has to be told (#63). Set on mount and cleared
  // on unmount, so the gate replacing this component takes the button with it.
  useEffect(() => {
    sidebarStore.setShellMounted(true);
    return () => sidebarStore.setShellMounted(false);
  }, []);

  const collapsed = useSyncExternalStore(sidebarStore.subscribe, sidebarStore.collapsed);
  const mobileOpen = useSyncExternalStore(sidebarStore.subscribe, sidebarStore.mobileOpen);
  const navigateAnd = (go: () => void) => {
    // A drawer that stays open across a navigation covers the pane the tap
    // just asked for — the old web closes its overlay the same way.
    sidebarStore.setMobileOpen(false);
    go();
  };

  /**
   * The sidebar's face: its menu, or the Revit Database panel taking it over.
   *
   * The panel is the chat's — it reads the chat's document context and inserts
   * into the chat's composer — so the chat pane renders it, through a portal,
   * into the slot below. What the shell owns is only whether it is open, and the
   * rule that it cannot be open off the chat, without a database to show, or
   * behind a hidden sidebar (`resolveSidebarView`).
   */
  const [requestedView, setSidebarView] = useState<SidebarView>("nav");
  const [panelSlot, setPanelSlot] = useState<HTMLDivElement | null>(null);
  const narrow = useMediaQuery(SIDEBAR_DRAWER_QUERY);
  const sidebarState = { narrow, collapsed, mobileOpen };
  // The same two reasons the rail's button was disabled on, in the same order.
  const databaseUnavailable = !effectiveProjectId
    ? t("projects.noneSelected")
    : selection.instance?.revitVersion == null
      ? t("revitIndex.missing")
      : null;
  const sidebarView = resolveSidebarView(requestedView, {
    onChat: route === "threads",
    databaseAvailable: databaseUnavailable === null,
    sidebarShown: sidebarShown(sidebarState),
  });
  // Forget a request that stopped being honoured, so the panel does not spring
  // back open by itself when the user returns to the chat or reopens the sidebar.
  useEffect(() => {
    if (sidebarView !== requestedView) setSidebarView(sidebarView);
  }, [sidebarView, requestedView]);
  const databaseOpen = sidebarView === "database";

  // The title bar's slot for the instance chips. Present only in the desktop
  // window; a plain browser has no title bar, so there the card stays in the sidebar.
  const chipSlot = useSyncExternalStore(titlebarSlot.subscribe, titlebarSlot.get);
  const instanceControls = { agent, hosts, selections, grouped, busy: chatBusy || familyBusy, pickFor, restriction: instanceRestriction };
  const startNewChat = () =>
    navigateAnd(() => {
      setEnteredWorkflow(null);
      setActiveThreadId(null);
      setNewChat((n) => n + 1);
      navigate("threads");
    });
  /** A workflow's card: a new conversation, in that workflow's view. */
  const enterWorkflow = (workflowId: string) =>
    navigateAnd(() => {
      setEnteredWorkflow(workflowId);
      setActiveThreadId(null);
      setNewChat((n) => n + 1);
      navigate("threads");
    });

  return (
    <div className={`app-shell${collapsed ? " is-collapsed" : ""}${mobileOpen ? " is-mobile-open" : ""}${databaseOpen ? " is-database" : ""}`}>
      {/* The narrow-viewport scrim — the old web's `bg-black/5 backdrop-blur`
          overlay behind its fixed sidebar. CSS keeps it inert at rail widths. */}
      <div className="app-sidebar-backdrop" aria-hidden="true" onClick={() => sidebarStore.setMobileOpen(false)} />
      <nav className={`app-sidebar${databaseOpen ? " is-database" : ""}`}>
        {/* Hidden, not unmounted, while the database panel has the sidebar: the
            agent's status element has to stay in the document, and nothing here
            should lose its state for a panel that is closed again in a moment. */}
        <div className="app-sidebar-main" hidden={databaseOpen}>
          <div className="app-brand">
            <img
              className="app-brand-logo"
              src="/bimgo-logo-sidebar.png"
              alt="Bimgo AI"
            />
            <img
              className="app-brand-icon"
              src="/bimgo-logo-symbol.svg"
              alt="Bimgo AI"
            />
          </div>

          <InstanceCard {...instanceControls} inTitlebar={chipSlot !== null} />

          <ul className="app-nav">
            {NAV_ROUTES.map((id) =>
              id === "projects" ? (
                // New chat and the database lead the tools; the project and its
                // conversations stay together in the group below this list.
                <Fragment key={id}>
                  <li>
                    <button type="button" className="app-nav-item" data-nav="new" aria-label={t("shell.nav.new")} title={t("shell.nav.new")} onClick={startNewChat}>
                      <span className="app-nav-icon">
                        <Icon path="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.375 2.625a1 1 0 0 1 3 3l-9.013 9.014a2 2 0 0 1-.853.505l-2.873.84a.5.5 0 0 1-.62-.62l.84-2.873a2 2 0 0 1 .506-.852z" />
                      </span>
                      <span className="app-nav-label">{t("shell.nav.new")}</span>
                    </button>
                  </li>
                  <li>
                    {/* No `data-nav`: this is not a route, and the desktop's smoke
                        check clicks every `[data-nav]` expecting a pane to answer. */}
                    <button
                      type="button"
                      className="app-nav-item is-database"
                      disabled={databaseUnavailable !== null}
                      aria-label={t("revitIndex.title")}
                      title={databaseUnavailable ?? t("revitIndex.title")}
                      onClick={() => {
                        // Not `navigateAnd`: that closes the narrow drawer, and
                        // the drawer is where the panel is about to open.
                        sidebarStore.setCollapsed(false);
                        setSidebarView(openDatabase());
                        navigate("threads");
                      }}
                    >
                      <span className="app-nav-icon">
                        <Icon path="M3 5a9 3 0 1 0 18 0 9 3 0 1 0-18 0M3 5v14a9 3 0 0 0 18 0V5M3 12a9 3 0 0 0 18 0" />
                      </span>
                      <span className="app-nav-label">{t("revitIndex.title")}</span>
                    </button>
                  </li>
                </Fragment>
              ) : (
                <li key={id}>
                  <button
                    type="button"
                    className="app-nav-item"
                    data-nav={id}
                    aria-current={route === id ? "page" : undefined}
                    aria-label={t(ROUTE_LABEL_KEYS[id])}
                    title={t(ROUTE_LABEL_KEYS[id])}
                    onClick={() => navigateAnd(() => navigate(id))}
                  >
                    <span className="app-nav-icon">{NAV_ICON[id]}</span>
                    <span className="app-nav-label">
                      {t(ROUTE_LABEL_KEYS[id])}
                      {BETA_ROUTES.has(id) ? <span className="app-beta-badge">Beta</span> : null}
                    </span>
                  </button>
                </li>
              ),
            )}
          </ul>

          <div className="app-project-chats">
            <div className="app-running-head app-project-label">{t("shell.project.label")}</div>
            <button
              type="button"
              className="app-nav-item app-project-row"
              data-nav="projects"
              aria-current={route === "projects" ? "page" : undefined}
              aria-label={selectedProject ? t("projects.currentProject", { name: selectedProject.name }) : t("shell.project.none")}
              title={selectedProject ? t("projects.currentProject", { name: selectedProject.name }) : t("shell.project.none")}
              onClick={() => navigateAnd(() => navigate("projects"))}
            >
              <span className="app-nav-icon">{NAV_ICON.projects}</span>
              <span className="app-project-text">
                <span className="app-project-name">{selectedProject?.name ?? t("shell.project.none")}</span>
              </span>
              <span className="app-project-caret" aria-hidden="true">›</span>
            </button>
            <div className="app-nav-history">
              <button
                type="button"
                className="app-nav-item"
                data-nav="chats"
                aria-current={route === "chats" ? "page" : undefined}
                aria-label={t("chats.title")}
                title={t("chats.title")}
                onClick={() => navigateAnd(() => navigate("chats"))}
              >
                <span className="app-nav-icon">{NAV_ICON.chats}</span>
                <span className="app-nav-label">{t("chats.title")}</span>
              </button>
            </div>

            <SidebarChats
              sessions={chatSessions}
              skillEdits={skillEdits}
              currentProjectId={effectiveProjectId}
              currentThreadId={route === "threads" ? activeThreadId : null}
              threads={threads}
              viewAllActive={route === "chats"}
              onOpen={openConversation}
              onOpenSkill={(id) =>
                navigateAnd(() => {
                  setOpenSkill({ id });
                  navigate("skills");
                })
              }
              onViewAll={() => navigateAnd(() => navigate("chats"))}
              onRetry={reloadThreads}
            />
          </div>
        </div>
        {/* The Revit Database panel's slot. The chat pane portals the panel in
            (`ThreadsPane`); held in state, not a ref, so the pane re-renders once
            the element exists. */}
        <div className="app-sidebar-panel" ref={setPanelSlot} hidden={!databaseOpen} />
        <div className="app-sidebar-bottom">
          <button
            type="button"
            className="app-nav-item app-report-issue"
            aria-label={t("ticket.open")}
            title={t("ticket.open")}
            onClick={() => setReportOpen(true)}
          >
            <span className="app-nav-icon">
              <Icon path="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2zM12 7v2M12 13h.01" />
            </span>
            <span className="app-nav-label">{t("ticket.open")}</span>
          </button>
          <AccountMenu
            name={name}
            orgs={orgs}
            currentOrgId={orgId}
            onSelectOrg={onSelectOrg}
            onOpenDashboard={() => navigate("dashboard")}
            onOpenSettings={() => navigate("settings")}
            settingsActive={route === "settings"}
            hidden={databaseOpen}
          />
        </div>
        {/* Mounted for the life of the shell, so a half-written report survives
            closing the dialog. */}
        <ReportIssueDialog open={reportOpen} onClose={() => setReportOpen(false)} token={token} orgId={orgId} />
      </nav>
      {/* The connected instance lives in the title bar whenever there is one:
          visible from every route, whether the sidebar is open or not. */}
      {chipSlot
        ? createPortal(<InstanceChips {...instanceControls} />, chipSlot)
        : null}

      <div className="app-main">
        <div className="app-content">
          {renderPanes(route, {
            info,
            workflowList,
            enabledWorkflowIds: enabledIds,
            onEnableWorkflow: (id: string) => setEnabled(id, true),
            betaSettings: <BetaFeaturesCard list={workflowList} enabledIds={enabledIds} onChange={setEnabled} />,
            agent,
            orgName,
            orgId,
            organization: currentOrg,
            token: session?.access_token ?? null,
            selectedProjectId: effectiveProjectId,
            // Opening a project binds it *and* goes to its chat — the Claude
            // behaviour: a project is a place you enter, not a checkbox. The
            // binding alone would leave the user on a list wondering what
            // clicking did.
            selectProject: (projectId: string) => {
              selectProject(projectId);
              navigate("threads");
            },
            projects,
            reloadProjects,
            // Only `project:create` and `project:manage_members` gate a
            // control here — the row menu is gated on `is_owner`, exactly as
            // `bimgo-ai-web`'s `ProjectsList` gates it. api-v2's
            // `require_action` remains the authority on every write.
            canCreateProject: hasAction(perms, "project:create"),
            canManageProjectMembers: hasAction(perms, "project:manage_members"),
            onCreateProject,
            onUpdateProject,
            onDeleteProject,
            selectedProjectName: selectedProject?.name ?? null,
            activeThreadId,
            onSelectThread: setActiveThreadId,
            hosts,
            plugins,
            targetSessionId: selection.instance?.sessionId ?? null,
            // One id per vendor — what the turn actually sends (D4, D9).
            targetSessions: targetSessions(selections),
            selection,
            selections,
            onBusyChange: setChatBusy,
            chatSessions,
            newChat,
            threads,
            reloadThreads,
            onOpenThread: (threadId: string) => openConversation(threadId, effectiveProjectId),
            enteredWorkflow,
            onEnterWorkflow: enterWorkflow,
            onOpenWorkflows: () => navigate("workflows"),
            onOpenPlugins: () => navigate("plugins"),
            databaseSlot: panelSlot,
            databaseOpen,
            onCloseDatabase: () => setSidebarView(closeDatabase()),
            skillEdits,
            onInstanceRestriction: setInstanceRestriction,
            openSkill,
            onOpenSkillHandled: openSkillHandled,
            selfUserId: session?.user?.id ?? null,
            onFamilyBusyChange: setFamilyBusy,
            perms,
          })}
        </div>
      </div>
    </div>
  );
}

/**
 * The bottom-left account control. The name is the trigger: clicking it opens a
 * menu with the organisations (the current one checked) and Settings, replacing
 * the old gear button. Closes on an outside click or Escape.
 */
function SignOutIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="m16 17 5-5-5-5M21 12H9" />
    </svg>
  );
}

function AccountMenu({
  name,
  orgs,
  currentOrgId,
  onSelectOrg,
  onOpenDashboard,
  onOpenSettings,
  settingsActive,
  hidden,
}: {
  name: string | null;
  orgs: { id: string; name: string }[];
  currentOrgId: string | null;
  onSelectOrg: (id: string) => void;
  onOpenDashboard: () => void;
  onOpenSettings: () => void;
  settingsActive: boolean;
  hidden: boolean;
}) {
  const { t } = useLocale();
  const { signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const currentOrg = orgs.find((o) => o.id === currentOrgId) ?? orgs[0] ?? null;

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="app-account" ref={ref} hidden={hidden}>
      {open ? (
        <div className="account-menu" role="menu">
          {orgs.length > 0 ? (
            <>
              <div className="account-menu-label">{t("account.organizations")}</div>
              {orgs.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  role="menuitemradio"
                  aria-checked={o.id === (currentOrg?.id ?? null)}
                  className="account-menu-item"
                  onClick={() => {
                    onSelectOrg(o.id);
                    setOpen(false);
                  }}
                >
                  <span className="account-menu-org">{o.name}</span>
                  {o.id === (currentOrg?.id ?? null) ? (
                    <span className="account-menu-check" aria-hidden>
                      ✓
                    </span>
                  ) : null}
                </button>
              ))}
              <div className="account-menu-sep" />
            </>
          ) : null}
          {/* Both of these are about *you* — your usage, your account — which is
              what this row is for; neither belongs in the rail beside the work. */}
          <button
            type="button"
            role="menuitem"
            className="account-menu-item"
            onClick={() => {
              onOpenDashboard();
              setOpen(false);
            }}
          >
            {NAV_ICON.dashboard}
            {t("dashboard.title")}
          </button>
          <button
            type="button"
            role="menuitem"
            className="account-menu-item"
            onClick={() => {
              onOpenSettings();
              setOpen(false);
            }}
          >
            {NAV_ICON.settings}
            {t("settings.title")}
          </button>
          {/* Signing out belongs to the identity you clicked, not to a settings
              form — the same place Claude and every other shell of this shape
              keeps it. It sits below a rule and in red so it cannot be taken for
              one of the org rows above it. */}
          <div className="account-menu-sep" />
          <button
            type="button"
            role="menuitem"
            className="account-menu-item is-danger"
            data-action="sign-out"
            onClick={() => {
              setOpen(false);
              void signOut();
            }}
          >
            <SignOutIcon />
            {t("account.signOut")}
          </button>
        </div>
      ) : null}

      <button
        type="button"
        className="app-account-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-current={settingsActive ? "page" : undefined}
        aria-label={name ?? t("shell.account.notSignedIn")}
        title={name ?? t("shell.account.notSignedIn")}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="app-account-avatar">{initial(name)}</span>
        <span className="app-account-meta">
          <div className="app-account-name">{name ?? t("shell.account.notSignedIn")}</div>
        </span>
        <span className="app-account-caret" aria-hidden="true">›</span>
      </button>
    </div>
  );
}

type PaneContext = Parameters<typeof renderPane>[1];

/**
 * The chat, always mounted, and the pane for the route.
 *
 * The chat's turn lives in its pane (`useChatTurn`), whose unmount aborts the stream —
 * so rendering it only on the `threads` route stopped a running task the moment the
 * user opened Skills or Plugins (#189). It stays mounted and is hidden instead; coming
 * back finds the turn still running, or finished, where it was left.
 */
function renderPanes(route: RouteId, context: PaneContext): React.ReactNode {
  const onChat = route === "threads";
  const revit = selectionFor(context.selections, "revit");
  return (
    <>
      <div className="app-chat-host" hidden={!onChat}>
        <ThreadsPane
          agent={context.agent}
          projectId={context.selectedProjectId}
          orgId={context.orgId}
          userId={context.selfUserId}
          token={context.token}
          activeThreadId={context.activeThreadId}
          onSelectThread={context.onSelectThread}
          targetSessionId={context.targetSessionId}
          targetSessions={context.targetSessions}
          revitVersion={context.selection.instance?.revitVersion ?? null}
          onBusyChange={context.onBusyChange}
          mayCreateSkill={hasAction(context.perms, "skill:create")}
          visible={onChat}
          sessions={context.chatSessions}
          newChat={context.newChat}
          reloadThreads={context.reloadThreads}
          databaseSlot={context.databaseSlot}
          databaseOpen={context.databaseOpen}
          onCloseDatabase={context.onCloseDatabase}
          workflowId={context.enteredWorkflow}
          onOpenWorkflows={context.onOpenWorkflows}
          hosts={context.hosts}
          plugins={context.plugins}
          onOpenPlugins={context.onOpenPlugins}
        />
      </div>
      <FamilyBuilderHost
        visible={route === "family-builder"}
        agent={context.agent} token={context.token} orgId={context.orgId} userId={context.selfUserId}
        projectId={context.selectedProjectId} revit={revit}
        otherVendorSelected={!revit.instance && context.selection.instance !== null && context.selection.instance.host_kind !== "revit"}
        onBusyChange={context.onFamilyBusyChange}
      />
      {onChat || route === "family-builder" ? null : renderPane(route, context)}
    </>
  );
}

/**
 * Route → view, for every route but the chat (`renderPanes` mounts that one). The chat
 * owns its own full-height scroll and composer, so it is not wrapped in the centered
 * `.app-pane` the other routes use.
 */
function renderPane(
  route: RouteId,
  context: {
    workflowList: WorkflowListState;
    enabledWorkflowIds: readonly string[];
    onEnableWorkflow: (id: string) => void;
    betaSettings: React.ReactNode;
    info: AppInfo | null;
    agent: AgentTarget;
    orgName: string | null;
    orgId: string | null;
    /** The whole record, not just its id — Settings' wallet reads the
     *  subscription off it rather than fetching the org list a second time. */
    organization: Organization | null;
    token: string | null;
    selectedProjectId: string | null;
    selectProject: (projectId: string) => void;
    projects: ProjectsState;
    reloadProjects: () => void;
    canCreateProject: boolean;
    canManageProjectMembers: boolean;
    onCreateProject: (draft: ProjectDraft) => Promise<void>;
    onUpdateProject: (projectId: string, draft: ProjectDraft) => Promise<void>;
    onDeleteProject: (projectId: string) => Promise<void>;
    selectedProjectName: string | null;
    selfUserId: string | null;
    perms: PermissionsState;
    activeThreadId: string | null;
    onSelectThread: (threadId: string | null) => void;
    hosts: HostsView;
    plugins: PluginsController;
    targetSessionId: string | null;
    selection: Selection;
    selections: Selections;
    targetSessions: Partial<Record<HostKind, string>>;
    onBusyChange: (busy: boolean) => void;
    onFamilyBusyChange: (busy: boolean) => void;
    chatSessions: ChatSessions;
    newChat: number;
    threads: ThreadsState;
    reloadThreads: () => void;
    onOpenThread: (threadId: string) => void;
    enteredWorkflow: string | null;
    onEnterWorkflow: (workflowId: string) => void;
    onOpenWorkflows: () => void;
    onOpenPlugins: () => void;
    databaseSlot: HTMLElement | null;
    databaseOpen: boolean;
    onCloseDatabase: () => void;
    skillEdits: SkillEditSessions;
    /** The Skills pane narrows a vendor's picker to an open skill's supported years. */
    onInstanceRestriction: (restriction: InstanceRestriction | null) => void;
    openSkill: { id: string } | null;
    onOpenSkillHandled: () => void;
  },
): React.ReactNode {
  switch (route) {
    case "threads":
      // Mounted by `renderPanes` for the life of the shell.
      return null;
    case "workflows":
      return (
        <div className="app-pane is-wide">
          <WorkflowsPane
            agent={context.agent}
            list={context.workflowList}
            enabledIds={context.enabledWorkflowIds}
            onEnable={context.onEnableWorkflow}
            projectSelected={context.selectedProjectId !== null}
            onEnter={context.onEnterWorkflow}
          />
        </div>
      );
    case "family-builder":
      return null;
    case "chats":
      return (
        <div className="app-pane is-wide">
          <ChatsPane
            threads={context.threads}
            projectName={context.selectedProjectName}
            activeThreadId={context.activeThreadId}
            sessions={context.chatSessions}
            onOpen={context.onOpenThread}
            onRetry={context.reloadThreads}
          />
        </div>
      );
    case "projects":
      return (
        <div className="app-pane is-wide">
          <ProjectsPane
            projects={context.projects}
            selectedProjectId={context.selectedProjectId}
            onSelectProject={context.selectProject}
            onReload={context.reloadProjects}
            canCreate={context.canCreateProject}
            onCreate={context.onCreateProject}
            onUpdate={context.onUpdateProject}
            onDelete={context.onDeleteProject}
            canManageMembers={context.canManageProjectMembers}
            orgId={context.orgId}
            token={context.token}
            selfUserId={context.selfUserId}
          />
        </div>
      );
    case "skills":
      return (
        <div className="app-pane is-wide">
          <SkillsPane
            agent={context.agent}
            hosts={context.hosts}
            selection={context.selection}
            selections={context.selections}
            orgId={context.orgId}
            token={context.token}
            userId={context.selfUserId}
            projectId={context.selectedProjectId}
            perms={context.perms}
            skillEdits={context.skillEdits}
            openSkill={context.openSkill}
            onOpenSkillHandled={context.onOpenSkillHandled}
            onInstanceRestriction={context.onInstanceRestriction}
          />
        </div>
      );
    case "plugins":
      return (
        <div className="app-pane is-wide">
          <PluginsPane agent={context.agent} hosts={context.hosts} plugins={context.plugins} />
        </div>
      );
    case "dashboard":
      return (
        <div className="app-pane is-wide">
          <DashboardPane orgId={context.orgId} token={context.token} perms={context.perms} />
        </div>
      );
    case "settings":
      return (
        <div className="app-pane is-wide">
          <SettingsPane orgId={context.orgId} organization={context.organization} betaSettings={context.betaSettings} />
        </div>
      );
    case "update-password":
      // Unreachable in practice — a recovery link is answered by `App` before
      // the shell is built, and nothing in the nav points here. Rendering the
      // real page rather than a placeholder means a desktop user who arrives by
      // some other path gets the form instead of a note to the developer.
      return <UpdatePassword />;
  }
}

function displayName(session: Session | null): string | null {
  const user = session?.user;
  if (!user) return null;
  const meta = (user.user_metadata ?? {}) as Record<string, unknown>;
  const full = meta["full_name"] ?? meta["name"];
  if (typeof full === "string" && full.trim()) return full.trim();
  if (user.email) return user.email.split("@")[0] ?? null;
  return null;
}

function initial(name: string | null): string {
  return name ? name.charAt(0).toUpperCase() : "·";
}

function Icon({ path }: { path: string }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d={path} />
    </svg>
  );
}
